import React, { useRef, useCallback } from "react";

//  Heart icon
export const HeartIcon: React.FC<{ className?: string }> = ({
  className = "",
}) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

//  Check icon
export const CheckIcon: React.FC = () => (
  <div className="w-5 h-5 rounded-full bg-black flex items-center justify-center flex-shrink-0 mt-0.5">
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="white"
      strokeWidth="2.5"
      className="w-2.5 h-2.5"
    >
      <polyline points="2,6 5,9 10,3" />
    </svg>
  </div>
);

//  Section eyebrow
export const Eyebrow: React.FC<{
  children: React.ReactNode;
  light?: boolean;
}> = ({ children, light }) => (
  <p
    className={`text-xs font-medium tracking-[2.5px] uppercase mb-4 block ${light ? "text-pink-300" : "text-rose"}`}
  >
    {children}
  </p>
);

//  Reveal wrapper
export const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  direction?: "up" | "left" | "right" | "scale";
  delay?: number;
}> = ({ children, className = "", direction = "up", delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const initial: Record<string, string> = {
    up: "opacity-0 translate-y-9",
    left: "opacity-0 -translate-x-12",
    right: "opacity-0 translate-x-12",
    scale: "opacity-0 scale-90",
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-x-0 translate-y-0 scale-100" : initial[direction]} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

//  Stagger reveal container
export const StaggerReveal: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, i) => (
        <div
          className={`transition-all duration-600 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: visible ? `${i * 100}ms` : "0ms" }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

//  Phone flip card
export const PhoneFlip: React.FC<{
  frontSrc: string;
  backSrc: string;
  frontAlt?: string;
  backAlt?: string;
  delay?: number;
  size?: "md" | "sm";
}> = ({
  frontSrc,
  backSrc,
  frontAlt = "",
  backAlt = "",
  delay = 0,
  size = "md",
}) => {
  const dims = size === "sm" ? "w-[200px] h-[410px]" : "w-[240px] h-[490px]";
  const innerDims =
    size === "sm" ? "rounded-[30px] p-[9px]" : "rounded-[40px] p-3";
  const screenRadius = size === "sm" ? "rounded-[22px]" : "rounded-[28px]";

  return (
    <div className={`${dims} mx-auto`} style={{ perspective: "1000px" }}>
      <div
        className={`relative w-full h-full bg-black ${innerDims} shadow-[0_40px_80px_rgba(0,0,0,0.18)] overflow-hidden`}
        style={{
          transformStyle: "preserve-3d",
          animation: `autoFlip 6s ease-in-out ${delay}ms infinite`,
        }}
      >
        {/* Front */}
        <div
          className={`absolute inset-0 overflow-hidden ${screenRadius} ${innerDims}`}
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          <img
            src={frontSrc}
            alt={frontAlt}
            className="w-full h-full object-cover block"
          />
        </div>
        {/* Back */}
        <div
          className={`absolute inset-0 overflow-hidden ${screenRadius} ${innerDims}`}
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <img
            src={backSrc}
            alt={backAlt}
            className="w-full h-full object-cover block"
          />
        </div>
      </div>
    </div>
  );
};

//  Feature bullet
export const Bullet: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <li className="flex items-start gap-3 text-sm">
    <CheckIcon />
    <span>{children}</span>
  </li>
);

//  3D Tilt card wrapper
export const TiltCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = ({ children, className = "", style }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const shineRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const hw = rect.width / 2;
    const hh = rect.height / 2;
    const rotY = ((cx - hw) / hw) * 12;
    const rotX = -((cy - hh) / hh) * 10;
    card.style.transform = `perspective(1000px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale(1.03)`;
    card.style.boxShadow = `${-rotY * 1.5}px ${rotX * 1.5}px 40px rgba(0,0,0,0.15)`;
    if (shineRef.current) {
      shineRef.current.style.background = `radial-gradient(circle at ${cx}px ${cy}px, rgba(255,255,255,0.18) 0%, transparent 65%)`;
      shineRef.current.style.opacity = "1";
    }
  }, []);

  const handleLeave = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform =
      "perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)";
    card.style.boxShadow = "";
    if (shineRef.current) shineRef.current.style.opacity = "0";
  }, []);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`relative overflow-hidden transition-shadow duration-300 ${className}`}
      style={{
        transformStyle: "preserve-3d",
        willChange: "transform",
        ...style,
      }}
    >
      <div
        ref={shineRef}
        className="absolute inset-0 pointer-events-none z-10 rounded-[inherit] opacity-0 transition-opacity duration-200"
      />
      {children}
    </div>
  );
};
